import React, { useEffect, useState } from 'react';
import useRequest from 'src/utils/hooks/useRequest';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { AUTH_ORIGIN } from 'src/constants/config';
import { useRouter } from 'next/router';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
import { useMarketingOnboardingSafe } from 'src/contexts/marketingOnboarding';

export interface SocialLoginButtonProps {
  provider: 'google-sso' | 'facebook-sso';
  isRegister: boolean;
  resetPasswordToken?: string;
  returnUrl?: string;
  onClose?: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface UseSocialLoginResult {
  handleSignIn: (event: React.MouseEvent) => Promise<void>;
  inAppBrowser: boolean;
  showTooltip: boolean;
  marketplace: any;
  upIn: string;
}

const useSocialLogin = ({
  provider,
  isRegister,
  resetPasswordToken,
  returnUrl,
  onClose,
  onSuccess,
  onError,
}: SocialLoginButtonProps): UseSocialLoginResult => {
  const { api } = useRequest();
  const router = useRouter();
  const isAdminRoute = router.pathname.startsWith('/slug_admin');
  const showToast = useSingleToast();
  const { marketplace, hostname } = useMarketplaceContext();
  const marketingOnboardingContext = useMarketingOnboardingSafe();
  const [inAppBrowser, setInAppBrowser] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const upIn = isRegister ? 'Sign up' : 'Log in';

  useEffect(() => {
    const isInAppBrowser = () => {
      if (typeof window === 'undefined' || !('userAgent' in window.navigator)) return false;
      const ua = window.navigator.userAgent;
      if (!ua) return false;
      return ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1 || ua.indexOf('Instagram') > -1;
    };

    if (isInAppBrowser()) {
      setInAppBrowser(true);
    }
  }, []);

  const handleSignIn = async (event: React.MouseEvent) => {
    event.preventDefault();

    if (!marketplace && !isAdminRoute) return;

    try {
      // generate the ticket token, which will be usable for 10 minutes
      const redirectUri = `https://${hostname}/validate-ticket`;
      const payload: Record<string, string> = resetPasswordToken
        ? { reset_password_token: resetPasswordToken }
        : {};

      // Include marketing_slug if we're in the onboarding flow
      const marketingSlug = marketingOnboardingContext?.state?.marketing_slug;
      if (marketingSlug) {
        payload.marketing_slug = marketingSlug;
      }

      const ticketToken = await api.marketplace.sso.generateTicket(
        marketplace?.id ?? 0,
        redirectUri,
        payload,
      );

      if (!ticketToken) {
        const errorMessage = 'Social login currently unavailable. Please try again later.';
        showToast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
          sentryAlert: {
            error: new Error('No ticket token generated for social login'),
            level: 'error',
            extras: {
              marketplaceId: marketplace?.id,
            },
          },
        });
        onError?.(errorMessage);
        return;
      }

      localStorage.setItem('ticket', ticketToken);

      // Preserve returnUrl through OAuth flow for post-login redirect
      const queryReturnUrl = router.query.returnUrl as string | undefined;
      if (
        queryReturnUrl &&
        typeof queryReturnUrl === 'string' &&
        queryReturnUrl.startsWith('/') &&
        !queryReturnUrl.startsWith('//')
      ) {
        localStorage.setItem('social_auth_return_url', queryReturnUrl);
      } else if (
        returnUrl &&
        typeof returnUrl === 'string' &&
        returnUrl.startsWith('/') &&
        !returnUrl.startsWith('//')
      ) {
        localStorage.setItem('social_auth_return_url', returnUrl);
      }

      if (inAppBrowser) {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 5000);
      } else {
        onClose?.();

        // send the user to the auth server and trigger the signIn there
        window.location.href = `${AUTH_ORIGIN}/oauth-trigger?ticket=${encodeURIComponent(
          ticketToken,
        )}&name=${encodeURIComponent(marketplace?.name ?? '')}&provider=${provider}`;

        onSuccess?.();
      }
    } catch (error) {
      // Extract error details for better debugging
      const errorMessage = 'Failed to initiate social login. Please try again.';
      const actualErrorMessage =
        error instanceof Error ? error.message : String(error || 'Social login failed');

      showToast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error: error instanceof Error ? error : new Error(actualErrorMessage),
          level: 'error',
          tags: {
            feature: 'authentication',
            operation: 'social_login',
            provider,
          },
          extras: {
            marketplaceId: marketplace?.id,
            errorDetails: error,
          },
        },
      });
      onError?.(errorMessage);
    }
  };

  return {
    handleSignIn,
    inAppBrowser,
    showTooltip,
    marketplace,
    upIn,
  };
};

export default useSocialLogin;
